import type { TFunc } from '@/translations';
import type { TranslationKey } from '@/translations/types';
import { generationStyleMessage } from '@/api/ProductImages/generationVariants';

const defaultPresentation = {
  missing: 'imagery.variantMissing',
  retry: 'imagery.retryVariant',
  confirm: 'imagery.confirmVariant',
  confirmTitle: 'imagery.confirmVariantTitle',
  confirmDescription: 'imagery.confirmVariantDescription',
  pendingCount: 'imagery.variantPendingCount',
  succeededCount: 'imagery.variantSucceededCount',
  failedCount: 'imagery.variantFailedCount',
  unknownCount: 'imagery.variantUnknownCount',
  stoppedCount: 'imagery.variantStoppedCount',
} satisfies Record<string, TranslationKey>;

export const variantPresentation = (variantKey: string) =>
  variantKey === 'studio-grey'
    ? ({
        ...defaultPresentation,
        missing: 'imagery.greyMissing',
        retry: 'imagery.retryGrey',
        confirm: 'imagery.confirmGrey',
        confirmTitle: 'imagery.confirmGreyTitle',
        confirmDescription: 'imagery.confirmGreyDescription',
        pendingCount: 'imagery.greyPending',
        succeededCount: 'imagery.greySucceeded',
        failedCount: 'imagery.greyFailed',
        unknownCount: 'imagery.greyUnknown',
        stoppedCount: 'imagery.greyStopped',
      } as const)
    : defaultPresentation;

export const variantLabel = (t: TFunc, variantKey: string) => {
  const message = generationStyleMessage(variantKey);
  return message ? t(message) : variantKey;
};

export const variantImageLabel = (t: TFunc, variantKey: string) =>
  variantKey === 'studio-grey'
    ? t('imagery.greyImage')
    : variantLabel(t, variantKey);

export const variantRetryLabel = (t: TFunc, variantKey: string) =>
  t(variantPresentation(variantKey).retry, {
    style: variantLabel(t, variantKey),
  });

export const variantImageMessage = (
  t: TFunc,
  reason?: string | null,
  fallback: TranslationKey = 'imagery.variantProcessingFailed',
) => {
  const code = reason?.replace(
    /^Image variant generation is unavailable:\s*/,
    '',
  );
  const messages: Record<string, TranslationKey> = {
    CurrentImageChanged: 'imagery.variantSourceChanged',
    SourceImageChanged: 'imagery.variantSourceChanged',
    SourceChecksumChanged: 'imagery.variantSourceChanged',
    GenerationPending: 'imagery.variantPending',
    VariantGenerationPending: 'imagery.variantPending',
    UnsupportedVariant: 'imagery.variantUnsupported',
    ImageAvailable: 'imagery.variantAvailable',
    VariantImageAvailable: 'imagery.variantAvailable',
    SourceImageRequired: 'imagery.variantSourceRequired',
    SourceImageUnavailable: 'imagery.variantSourceUnavailable',
    ProductUnavailable: 'imagery.variantProductUnavailable',
    GenerationPaused: 'imagery.generationPaused',
    CustomerGenerationDisabled: 'imagery.customerGenerationDisabled',
    CustomerInactive: 'imagery.customerGenerationDisabled',
    ProviderNotConfigured: 'imagery.providerNotConfigured',
    ConsistencyRejected: 'imagery.variantConsistencyRejected',
    ProcessingFailed: 'imagery.variantProcessingFailed',
    ExecutionInterrupted: 'imagery.variantInterrupted',
    InterruptedNeedsReview: 'imagery.variantUnknownOutcome',
    ProviderOutcomeUnknown: 'imagery.variantUnknownOutcome',
    ModelRequestLimit: 'imagery.variantRequestLimit',
    ProductUpdateInProgress: 'imagery.requestConflict',
    IdentityUnresolved: 'imagery.identityUnresolvedDescription',
    IdentityConflict: 'imagery.identityConflictDescription',
    SourceIdentityUnverified: 'imagery.sourceIdentityUnverified',
    SourceResearchUnavailable: 'imagery.sourceResearchUnavailable',
    SourceImageSuspended: 'imagery.sourceImageSuspended',
  };
  return code && messages[code] ? t(messages[code]) : reason || t(fallback);
};
